/**
 * Plain words for the codes the system stores.
 *
 * The admin panel never shows an internal code (`ended_low_balance`,
 * `paid_credit_pending`, `listener.approve`…): every status, type and action
 * goes through {@link plain}, so the same thing reads the same way on every
 * page. A code with no entry is still made readable ("some_new_code" →
 * "Some new code") rather than shown raw.
 */
const WORDS: Record<string, string> = {
  // ---- Calls ----
  initiated: 'Starting',
  ringing: 'Ringing',
  connecting: 'Connecting',
  connected: 'In progress',
  ended_normal: 'Ended normally',
  ended_low_balance: 'Ended — coins ran out',
  ended_dropped: 'Dropped (connection lost)',
  ended_flagged: 'Stopped for safety',
  ended_rejected: 'Not answered / declined',
  failed: 'Failed',
  audio: 'Audio',
  video: 'Video',
  direct: 'Direct call',
  random: 'Quick connect',
  not_billable: 'Not charged',
  free_trial: 'Free first call',
  billed: 'Charged',
  unbilled: 'Not charged yet',
  verification: 'Test call',
  // call outcomes
  completed: 'Completed',
  unconnected: 'Never connected',
  declined: 'Declined',
  cancelled: 'Cancelled',
  no_answer: 'Missed — no answer',
  not_connected: 'Accepted, never connected',
  setup_failed: 'Could not start',
  in_progress: 'In progress',
  // who / why a call ended
  user: 'User',
  caller: 'Caller',
  listener: 'Listener',
  system: 'System',
  admin: 'Admin',
  hung_up: 'Hung up',
  low_balance: 'Coins ran out',
  connect_timeout: 'Took too long to connect',
  accept_window_timeout: 'Not answered in time',
  listener_reject: 'Listener declined',
  caller_cancelled: 'Caller cancelled',
  caller_app_gone: 'Stopped automatically',
  late_accept_refused: 'Late accept refused',
  listener_cancelled: 'Listener cancelled',
  superseded: 'Replaced by a newer call',
  client_setup_failed: 'Could not start on the phone',
  // ---- Listeners ----
  pending: 'Waiting for review',
  interview_scheduled: 'Interview scheduled',
  approved: 'Approved',
  rejected: 'Rejected',
  suspended: 'Suspended',
  dummy: 'Placeholder profile',
  peer: 'Peer listener',
  coach: 'Coach',
  clinical: 'Clinical expert',
  // ---- Employment ----
  full_time: 'Full time',
  part_time: 'Part time',
  present: 'Present',
  partial: 'Partial',
  absent: 'Absent',
  day_off: 'Day off',
  off_day_worked: 'Worked on a day off',
  holiday: 'Holiday',
  holiday_worked: 'Worked on a holiday',
  working: 'Working day',
  off: 'Off day',
  prorated: 'Prorated',
  fixed: 'Fixed',
  livestream: 'Live time',
  livestream_calls: 'Live + calls',
  live_calls: 'App-open + calls',
  online_calls: 'Callable + calls',
  statement_only: 'Statement only',
  withdrawable: 'Withdrawable',
  fixed_window: 'Fixed time window',
  flexible: 'Flexible',
  after_scheduled_hours: 'After the scheduled hours',
  after_shift_end: 'After the shift end',
  daily_threshold: 'After a daily threshold',
  same_rate: 'Same hourly rate',
  fixed_rate: 'Fixed rate',
  multiplier: 'Multiplier',
  automatic: 'Automatic',
  manual: 'Manual',
  end_of_day: 'End of work day',
  target_completed: 'When the daily target is completed',
  daily: 'Daily',
  on_target: 'On target',
  adjustment: 'Adjustment',
  credited: 'Credited to wallet',
  auto: 'Automatic',
  normal_day: 'Normal day',
  partial_day: 'Partial day',
  overtime_day: 'Overtime day',
  off_day_work: 'Work on a day off',
  holiday_work: 'Work on a holiday',
  // ---- ID documents ----
  uploaded: 'Waiting for review',
  verified: 'Verified',
  pan: 'PAN card',
  aadhaar: 'Aadhaar',
  licence: 'Licence',
  other: 'Other',
  pending_review: 'Waiting for review',
  // ---- Payouts ----
  pending_approval: 'Waiting for approval',
  processing: 'Being paid',
  settled: 'Paid',
  reversed: 'Returned',
  // ---- Recharges ----
  created: 'Started',
  attempted: 'Payment tried',
  captured: 'Paid',
  abandoned: 'Left unfinished',
  refunded: 'Refunded',
  paid_credit_pending: 'Paid — coins not added yet',
  razorpay: 'Razorpay',
  google_play: 'Google Play',
  credit: 'Money in',
  debit: 'Money out',
  reversal: 'Taken back',
  // ---- Safety reports ----
  actioned: 'Action taken',
  dismissed: 'Dismissed',
  low: 'Low',
  med: 'Medium',
  high: 'High',
  critical: 'Critical',
  call_session: 'Call',
  live_stream: 'Live stream',
  party_room: 'Party',
  chat_message: 'Chat message',
  profile: 'Profile',
  // ---- App problems ----
  open: 'Open',
  acknowledged: 'Being looked at',
  resolved: 'Fixed',
  ignored: 'Ignored',
  info: 'Note',
  warning: 'Warning',
  error: 'Problem',
  fatal: 'App crashed',
  crash: 'App crash',
  network: 'Internet / connection',
  call: 'Call',
  permission: 'Phone permission',
  navigation: 'Moving between screens',
  ui: 'Screen display',
  ios: 'iPhone',
  android: 'Android',
  web: 'Website',
  RIVERPOD_LIFECYCLE: 'App screen problem',
  LISTENERS_API: 'Listener list problem',
  DIO_NETWORK: 'Internet problem',
  AGORA: 'Call service problem',
  WEBRTC: 'Call connection problem',
  SOCKET: 'Live connection problem',
  AUTH: 'Login problem',
  ADMIN_PANEL: 'Admin panel problem',
  BACKEND: 'Server problem',
  UNKNOWN: 'Unknown',
  // ---- Live / party ----
  scheduled: 'Scheduled',
  live: 'Live now',
  paused: 'Paused',
  ended: 'Ended',
  force_ended: 'Stopped by admin',
  reaped: 'Closed automatically',
  active: 'Active',
  expired: 'Expired',
  closing: 'Closing',
  // ---- Referrals ----
  rewarded: 'Reward paid',
  capped: 'Limit reached',
  void: 'Cancelled',
  // ---- Admin roles ----
  super_admin: 'Owner',
  sub_admin: 'Team member',
  ops: 'Operations',
  finance: 'Finance',
  moderator: 'Safety reviewer',
  kyc_reviewer: 'ID reviewer',
  kyc_viewer: 'ID viewer',
  agency: 'Agency partner',
  // ---- App state ----
  foreground: 'Open on screen',
  background: 'In the background',
  closed: 'Closed',
  // ---- What an admin did (activity log) ----
  'listener.approve': 'Approved a listener',
  'listener.reject': 'Rejected a listener',
  'listener.suspend': 'Suspended a listener',
  'listener.unsuspend': 'Lifted a suspension',
  'listener.schedule_interview': 'Scheduled an interview',
  'listener.set_rate': 'Changed a listener’s rate',
  'listener.dummy': 'Changed a placeholder profile',
  'listener.hot.set': 'Changed a hot buddy mark',
  'listener.verify_professional': 'Changed professional verification',
  'user.wallet_adjust': 'Adjusted a user’s coins',
  'user.block': 'Blocked a user',
  'user.unblock': 'Unblocked a user',
  'user.delete': 'Deleted a user',
  'user.force_logout': 'Signed a user out',
  'user.gender.set': 'Changed a user’s gender',
  'user.reveal_phone': 'Viewed a full phone number',
  'payout.approve': 'Approved a payout',
  'payout.reject': 'Rejected a payout',
  'payout.mark_paid': 'Marked a payout as paid',
  'kyc.verify': 'Verified an ID document',
  'kyc.reject': 'Rejected an ID document',
  'kyc.view': 'Viewed an ID document',
  'kyc.view_original': 'Viewed an original ID document',
  'report.tds': 'Downloaded the tax (TDS) report',
  'welcome_rule.create': 'Created a welcome rule',
  'welcome_rule.update': 'Edited a welcome rule',
  'welcome_rule.delete': 'Deleted a welcome rule',
  'welcome_rule.activate': 'Turned a welcome rule on',
  'welcome_rule.deactivate': 'Turned a welcome rule off',
  'welcome_rule.reorder': 'Reordered the welcome rules',
  'welcome_rule.settings.update': 'Changed the welcome banner settings',
  'promo.create': 'Created a promotion campaign',
  'promo.update': 'Edited a promotion campaign',
  'promo.activate': 'Turned a promotion campaign on',
  'promo.deactivate': 'Turned a promotion campaign off',
  'promo.archive': 'Archived a promotion campaign',
  'promo.unarchive': 'Restored an archived promotion campaign',
  'promo.image_upload_url': 'Uploaded a promotion image',
  'listener.employment_enable': 'Made a listener an employee',
  'listener.employment_update': 'Edited employment details',
  'listener.employment_pause': 'Paused an employment',
  'listener.employment_resume': 'Resumed an employment',
  'listener.employment_end': 'Ended an employment',
  'listener.employment_terms_create': 'Saved new employment terms',
  'listener.employment_terms_correct': 'Backdated an employment terms correction',
  'listener.employment_attendance_adjust': 'Adjusted an employee’s worked time',
  'listener.employment_overtime_approve': 'Approved overtime',
  'listener.employment_overtime_reject': 'Rejected overtime',
  'listener.employment_attendance_recompute': 'Recomputed an attendance day',
  'employment.holiday_create': 'Added an employment holiday',
  'employment.holiday_delete': 'Removed an employment holiday',
  'listener.employment_settlement_create': 'Recorded an employee pay settlement',
  'listener.employment_wallet_credit': 'Credited employee pay to a wallet',
  // targets
  Listener: 'Listener',
  User: 'User',
  PayoutRequest: 'Payout',
  KycDocument: 'ID document',
  CallSession: 'Call',
  SystemSetting: 'Setting',
  WelcomeRule: 'Welcome rule',
  PromoCampaign: 'Promotion campaign',
  GiftCatalog: 'Gift',
  PaymentOrder: 'Recharge',
  ModerationFlag: 'Safety report',
  ClientIncident: 'App problem',
  TdsLedger: 'Tax report',
  EmploymentHoliday: 'Employment holiday',
};

/** "some_new.code" → "Some new code". */
function readable(code: string): string {
  const words = code
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '—';
}

/** The plain-words name of a stored code. */
export function plain(code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') return '—';
  return WORDS[code] ?? readable(code);
}
